import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import TrashIcon from '@/assets/icons/Monitoring/eva_trash-2-outline.svg?react';
import MoreIcon from '@/assets/icons/Monitoring/MoreIcon.svg?react';
import DeliveryTruckIcon from '@/assets/icons/ProductOptimization/DeliveryTruckIcon.svg?react';
import { Dialog, DialogContent, DialogClose } from '@/components/atoms/Dialog';
import { useMembershipPreviewMode } from '@/components/membership/MembershipContentLock';
import { Button } from '@/components/atoms/PcButton';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/molecules/DropdownMenu';
import { useToast } from '@/hooks/V3/useToast';
import { useAppSelector } from '@/redux/store';
import { useRecoilValue } from 'recoil';
import { deviceUuidState } from '@/store/atom';
import { useLog } from '@/hooks/V3/useLog';
import { useDeleteOptimization } from './useDeleteOptimization';
import DiagnosisFailedDialog from './DiagnosisFailedDialog';
import GoodsImage from './GoodsImage';
import type { DiagnosisListItem } from '@/types/productOptimization';
import { useProductOptimization } from './ProductOptimizationContext';
import { useMembershipProductOptimizationPreviewTutorial } from '@/components/membership/MembershipProductOptimizationPreviewTutorial';

interface Props {
  diagnosis: DiagnosisListItem;
}

const DiagnosisCard = ({ diagnosis }: Props) => {
  const navigate = useNavigate();
  const isPreviewMode = useMembershipPreviewMode();
  const cplatToken = useAppSelector((state) => state.user.CplatToken);
  const userNo = useAppSelector((s) => s.user.UserNo);
  const deviceUuid = useRecoilValue(deviceUuidState);
  const { setUserEventHistory } = useLog(userNo);
  const { showToast } = useToast();
  const { refetchList } = useProductOptimization();
  const { openTutorial } = useMembershipProductOptimizationPreviewTutorial();
  const { mutate: deleteMut, isPending: isDeleting } = useDeleteOptimization();

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [failedOpen, setFailedOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const isAnalyzing = diagnosis.status === 'Analyzing';
  const isFailed = diagnosis.status === 'Failed';

  const handleClick = () => {
    if (isAnalyzing) return;
    if (isFailed) {
      setUserEventHistory({
        eventName: 'optimization_diagnosis_fail_web',
        eventValue: String(diagnosis.id),
        deviceUuid,
      });
      setFailedOpen(true);
      return;
    }
    navigate(`?select=${diagnosis.id}`);
  };

  const deleteDiagnosis = (onDone?: () => void) => {
    deleteMut(
      { cplatToken, diagnosisId: diagnosis.id },
      {
        onSuccess: () => {
          setUserEventHistory({
            eventName: 'optimization_diagnosis_delete_web',
            eventValue: String(diagnosis.id),
            deviceUuid,
          });
          showToast('success', '진단 상품을 삭제했어요.');
          onDone?.();
          refetchList();
        },
        onError: () => {
          showToast('warning', '삭제 중 오류가 발생했어요.');
        },
      },
    );
  };

  const handleDelete = () => deleteDiagnosis(() => setConfirmOpen(false));
  const handleDeleteFromFailedDialog = () => deleteDiagnosis(() => setFailedOpen(false));

  return (
    <>
      <div
        data-membership-preview-interactive={isPreviewMode && diagnosis.id < 0 ? 'true' : undefined}
        onClick={handleClick}
        className={`flex items-start justify-between gap-[12px] rounded-[12px] bg-white p-[16px] ${
          isAnalyzing ? 'cursor-default' : 'cursor-pointer'
        }`}
      >
        <div className="flex flex-1 gap-[12px]">
          <GoodsImage
            src={diagnosis.goodsImageUrl}
            alt={diagnosis.goodsName}
            className="h-[80px] w-[80px] shrink-0 rounded-[8px] border border-gr-100 object-cover"
          />
          <div className="flex flex-1 flex-col gap-[8px]">
            <div className="flex flex-col gap-[4px]">
              <div className={`flex items-center gap-[6px] ${isAnalyzing ? 'opacity-50' : ''}`}>
                <span className="shrink-0 rounded-[8px] bg-point-100 px-[6px] py-[2px] text-point-500 Caption1M12">
                  {diagnosis.id < 0 ? '예시상품' : '셀러박스'}
                </span>
                <span className="line-clamp-1 text-gr-800 Body1S16">
                  {diagnosis.goodsName ?? '-'}
                </span>
              </div>
              <span className="text-gr-600 Body5M14">
                진단일 : {new Date(diagnosis.diagnosisDate).toLocaleDateString('ko-KR')}
              </span>
              {isAnalyzing && (
                <span className="text-gr-800 Body1S16">
                  🔍 상품을 분석하고 있어요. 잠시만 기다려주세요.
                </span>
              )}
            </div>

            {!isAnalyzing && (
              <>
                <div className="flex items-end gap-[12px]">
                  <div className="flex items-center gap-[4px]">
                    <span className="text-gr-800 Body1S16">
                      {diagnosis.salePrice.toLocaleString()}원
                    </span>
                    {diagnosis.originalPrice > diagnosis.salePrice && (
                      <span className="text-gr-500 line-through Caption3M10">
                        {diagnosis.originalPrice.toLocaleString()}원
                      </span>
                    )}
                  </div>
                  {diagnosis.deliveryFee > 0 && (
                    <div className="flex items-center gap-[6px] text-gr-800 Body2M16">
                      <DeliveryTruckIcon className="h-[20px] w-[20px]" />
                      <span>{diagnosis.deliveryFee.toLocaleString()} 원</span>
                    </div>
                  )}
                </div>

                <div className="flex w-fit items-center gap-[24px] rounded-[8px] bg-gr-50 p-[10px]">
                  <div className="flex items-center gap-[10px]">
                    <span className="text-gr-600 Body5M14">찜 수</span>
                    <span className="text-gr-700 Body4S14">
                      {diagnosis.wishCount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-[10px]">
                    <span className="text-gr-600 Body5M14">리뷰 수</span>
                    <span className="text-gr-700 Body4S14">
                      {diagnosis.reviewCount.toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center gap-[10px]">
                    <span className="text-gr-600 Body5M14">평점</span>
                    <span className="text-gr-700 Body4S14">{diagnosis.rating.toFixed(1)}</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              onClick={(e) => e.stopPropagation()}
              data-membership-preview-interactive={isPreviewMode ? 'true' : undefined}
              className="flex shrink-0 items-center justify-center rounded-[8px] border border-gr-200 bg-white p-[7px] text-gr-700 hover:bg-gr-50"
              aria-label="더보기"
            >
              <MoreIcon />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            onClick={(e) => e.stopPropagation()}
            className="min-w-[100px]"
          >
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                setDropdownOpen(false);
                if (openTutorial('deleteDiagnosis')) return;

                setConfirmOpen(true);
              }}
              className="cursor-pointer gap-[4px] text-gr-700 Body5M14"
            >
              <TrashIcon />
              삭제하기
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent noCloseBtn className="w-[360px] p-[30px]">
          <div className="flex flex-col gap-[20px]">
            <div className="flex flex-col gap-[4px]">
              <div className="text-gr-900 Title3S18">진단 상품을 삭제할게요</div>
              <div className="whitespace-pre-line text-gr-700 Body5M14">
                {
                  '상품이 삭제된 기간 동안\n해당 상품은 모니터링 진행되지 않아요.\n그래도 삭제할까요?'
                }
              </div>
            </div>
            <div className="flex w-full gap-[12px]">
              <DialogClose asChild>
                <Button variant="muted" className="h-[48px] flex-1">
                  닫기
                </Button>
              </DialogClose>
              <Button
                variant="default"
                className="h-[48px] flex-1"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                삭제
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <DiagnosisFailedDialog
        open={failedOpen}
        onOpenChange={setFailedOpen}
        onDelete={handleDeleteFromFailedDialog}
        isDeleting={isDeleting}
      />
    </>
  );
};

export default DiagnosisCard;
